import { useState } from 'react';
import { recordingState } from '@/lib/recording-status';
import { Input } from '@/components/ui/form';
import { ArrowDownLeft, ArrowUpRight, Pause, PhoneMissed, Play } from 'lucide-react';
import { useAudio } from '@/app/audio';
import { useEmployees } from '@/app/workspace';
import { RANGE_PRESETS, rangeLabel, toDateInput, type DateRangeValue } from '@/lib/dates';
import type { Call } from '@/lib/types';
import { formatDuration } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Select, Status, Modal, Field } from '@/components/ui/system';
export function DateFilter({
  value,
  onChange,
}: {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
}) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const choose = () => {
    setFrom(value.from || toDateInput(new Date()));
    setTo(value.to || toDateInput(new Date()));
    setOpen(true);
  };
  const invalid = !from || !to || from > to;
  return (
    <>
      <div className="toolbar-filter">
        <Select
          label="Date range"
          value={value.preset}
          onChange={(preset) =>
            preset === 'custom' ? choose() : onChange({ preset: preset as DateRangeValue['preset'] })
          }
          options={[
            ...RANGE_PRESETS.map((p) => ({ value: p.id, label: p.label })),
            { value: 'custom', label: value.preset === 'custom' ? rangeLabel(value) : 'Custom dates…' },
          ]}
        />
        {value.preset === 'custom' && (
          <Button variant="ghost" size="xs" aria-label="Edit date range" onClick={choose}>
            Edit
          </Button>
        )}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Choose date range"
        description="Both dates are included. Dates use your local time."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={invalid}
              onClick={() => {
                onChange({ preset: 'custom', from, to });
                setOpen(false);
              }}
            >
              Apply range
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <Field label="From">
            <Input
              type="date"
              value={from}
              max={to || undefined}
              aria-invalid={!!from && !!to && from > to}
              aria-describedby="date-range-hint"
              onInput={(e) => setFrom(e.currentTarget.value)}
              onChange={(e) => setFrom(e.target.value)}
            />
          </Field>
          <Field label="To">
            <Input
              type="date"
              value={to}
              min={from || undefined}
              aria-invalid={!!from && !!to && from > to}
              aria-describedby="date-range-hint"
              onInput={(e) => setTo(e.currentTarget.value)}
              onChange={(e) => setTo(e.target.value)}
            />
          </Field>
        </div>
        <p id="date-range-hint" className="mt-3 text-xs text-fg-muted" role="status">
          {!from || !to
            ? 'Choose both a start and an end date.'
            : from > to
              ? 'The end date must be on or after the start date.'
              : 'Apply range to update your results.'}
        </p>
      </Modal>
    </>
  );
}
export function EmployeeFilter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const q = useEmployees();
  return (
    <div className="toolbar-filter">
      <Select
        label="Employee"
        value={value}
        onChange={onChange}
        options={[
          {
            value: '',
            label: q.isPending ? 'Loading employees…' : q.error ? 'Employees unavailable' : 'All employees',
          },
          ...(q.data || []).map((e) => ({ value: String(e.id), label: e.name })),
        ]}
        disabled={q.isPending || !!q.error}
      />
      {q.error && (
        <Button variant="ghost" size="xs" aria-label="Retry loading employees" onClick={() => q.refetch()}>
          Retry
        </Button>
      )}
    </div>
  );
}
export function CallOutcome({ call }: { call: Pick<Call, 'type' | 'duration'> }) {
  const missed = ['missed', 'rejected', 'blocked'].includes(call.type) || call.duration === 0;
  const label =
    call.type === 'rejected'
      ? 'Rejected'
      : call.type === 'blocked'
        ? 'Blocked'
        : call.type === 'missed'
          ? 'Missed'
          : call.type === 'outgoing'
            ? call.duration
              ? 'Outgoing'
              : 'Not picked up'
            : call.duration
              ? 'Incoming'
              : 'Missed';
  const Icon = missed ? PhoneMissed : call.type === 'outgoing' ? ArrowUpRight : ArrowDownLeft;
  return (
    <span className="inline-flex items-center gap-2">
      <Icon className={missed ? 'text-danger' : 'text-primary'} size={15} />
      <span>
        {label}
        <small className="subline">{call.duration ? formatDuration(call.duration) : 'Not connected'}</small>
      </span>
    </span>
  );
}
export function PlayCall({ call }: { call: Call }) {
  const audio = useAudio();
  // Each state says why there is no audio: "Uploading" forever hid storage, plan and phone problems.
  const state = recordingState(call);
  if (state)
    return state.tone === 'muted' ? (
      <span className="text-fg-subtle text-xs" title={state.title}>
        {state.label}
      </span>
    ) : (
      <span title={state.title}>
        <Status tone={state.tone === 'warning' ? 'warning' : undefined}>{state.label}</Status>
      </span>
    );
  const active = audio.track?.id === String(call.id) && audio.playing;
  return (
    <Button
      variant="secondary"
      size="xs"
      aria-label={active ? 'Pause recording' : 'Play recording'}
      onClick={(e) => {
        e.stopPropagation();
        audio.play({
          id: String(call.id),
          url: call.recordingUrl!,
          title: call.name || call.phone,
          subtitle: call.employeeName || undefined,
          phone: call.phone,
        });
      }}
    >
      {active ? <Pause /> : <Play />}
      {formatDuration(call.duration)}
    </Button>
  );
}
